'use client';

import { FormField } from '@/features/forms/FormPrimitives';
import { HubPill } from '@/features/hub/shared/HubComponents';
import readHubMessage from '@/features/hub/shared/readHubMessage';
import { clientFetch } from '@/lib/api/clientFetch';
import { useState } from 'react';
import { Image, Spinner } from 'react-bootstrap';
import { canCurrentUserManageHubTeam } from '../teamRoles';

function formatInviteTime(value, locale, fallbackLabel = '') {
  if (!value) {
    return fallbackLabel;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return fallbackLabel;
  }

  try {
    return new Intl.DateTimeFormat(locale || 'en', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  } catch {
    return date.toLocaleString();
  }
}

export default function HubTeamInvitePanel({
  team,
  currentUser,
  initialInvites = [],
  locale,
  messages = {},
}) {
  const canInvite = Boolean(
    currentUser && canCurrentUserManageHubTeam(team, currentUser),
  );

  const [invites, setInvites] = useState(
    Array.isArray(initialInvites) ? initialInvites : [],
  );
  const [inviteUserNumber, setInviteUserNumber] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [isLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [activeInviteKey, setActiveInviteKey] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const msg = (path, fallback = '', replacements = {}) =>
    readHubMessage(messages, path, fallback, replacements);

  if (!canInvite) {
    return null;
  }

  const sendInvite = async (event) => {
    event.preventDefault();

    if (!inviteUserNumber.trim()) {
      setErrorMessage(
        msg(
          'teams.invites.errors.userNumberRequired',
          'Hub user ID is required.',
        ),
      );
      return;
    }

    setErrorMessage('');
    setStatusMessage('');
    setIsSubmitting(true);

    try {
      const response = await clientFetch(
        `/api/hub/teams/${team.teamNumber}/invites`,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            userNumber: inviteUserNumber.trim(),
            message: inviteMessage.trim(),
          }),
        },
      );

      const inviteLabel =
        response?.displayName || response?.name || response?.userNumber;

      setInvites((currentInvites) => [
        response,
        ...currentInvites.filter(
          (invite) => invite.userNumber !== response?.userNumber,
        ),
      ]);
      setInviteUserNumber('');
      setInviteMessage('');
      setStatusMessage(
        msg('teams.invites.sentToast', 'Invite sent to {{name}}.', {
          name: inviteLabel || inviteUserNumber.trim(),
        }),
      );
    } catch (error) {
      setErrorMessage(
        error?.data?.message ||
          error?.message ||
          msg(
            'teams.invites.errors.sendInvite',
            'Unable to send the team invite.',
          ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelInvite = async (userNumber) => {
    const requestKey = `${userNumber}:cancel`;
    const invite = invites.find((entry) => entry.userNumber === userNumber);
    const inviteLabel = invite?.displayName || invite?.name || userNumber;

    setErrorMessage('');
    setStatusMessage('');
    setActiveInviteKey(requestKey);

    try {
      await clientFetch(
        `/api/hub/teams/${team.teamNumber}/invites/${encodeURIComponent(userNumber)}`,
        {
          method: 'DELETE',
        },
      );

      setInvites((currentInvites) =>
        currentInvites.filter((entry) => entry.userNumber !== userNumber),
      );
      setStatusMessage(
        msg(
          'teams.invites.canceledToast',
          'Canceled the invite for {{name}}.',
          { name: inviteLabel },
        ),
      );
    } catch (error) {
      setErrorMessage(
        error?.data?.message ||
          error?.message ||
          msg(
            'teams.invites.errors.cancelInvite',
            'Unable to cancel the team invite.',
          ),
      );
    } finally {
      setActiveInviteKey('');
    }
  };

  return (
    <section className="app-panel hub-action-panel hub-action-panel--teams">
      <div className="hub-action-panel__header">
        <div>
          <p className="hub-section__eyebrow hub-section__eyebrow--teams">
            {msg('teams.invites.eyebrow', 'Direct recruit')}
          </p>
          <h2>{msg('teams.invites.title', 'Invite players to this team')}</h2>
        </div>

        <span className="hub-pill hub-pill--games">
          {msg('teams.invites.pendingCount', '{{count}} invites', {
            count: invites.length,
          })}
        </span>
      </div>

      <p className="shop-panel__meta">
        {msg(
          'teams.invites.hint',
          'Send a direct invitation by Hub user ID when you already know who should join this roster.',
        )}
      </p>

      {statusMessage ? (
        <div className="auth-message auth-message--success">
          {statusMessage}
        </div>
      ) : null}

      {errorMessage ? (
        <div className="auth-message auth-message--error">{errorMessage}</div>
      ) : null}

      <form
        className="hub-inline-stack hub-team-invite-form"
        onSubmit={sendInvite}
      >
        <div className="hub-team-invite-fields">
          <FormField
            label={msg('teams.invites.userNumberLabel', 'Hub user ID')}
            name="inviteUserNumber"
            showRequiredAsterisk
            value={inviteUserNumber}
            onChange={(event) => setInviteUserNumber(event.target.value)}
            placeholder={msg(
              'teams.invites.userNumberPlaceholder',
              'Example: USR-250101-120000-123',
            )}
          />

          <FormField
            as="textarea"
            label={msg('teams.invites.messageLabel', 'Invite note')}
            name="inviteMessage"
            rows={4}
            value={inviteMessage}
            onChange={(event) => setInviteMessage(event.target.value)}
            placeholder={msg(
              'teams.invites.messagePlaceholder',
              'Optional: explain the role, event, or reason for the invite.',
            )}
            inputClassName="ps-3"
          />
        </div>

        <div className="account-form__actions">
          <button
            type="submit"
            className="app-button app-button--primary gap-2"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Spinner size="sm" />
                {msg('teams.invites.sending', 'Sending...')}
              </>
            ) : (
              <>
                <Image src="/icons/send.png" alt="" width={16} height={16} />
                {msg('teams.invites.sendCta', 'Send invite')}
              </>
            )}
          </button>
        </div>
      </form>

      {isLoading ? (
        <p className="shop-panel__meta mt-3">
          {msg('teams.invites.loading', 'Loading team invitations...')}
        </p>
      ) : invites.length ? (
        <div className="hub-request-list">
          {invites.map((invite) => {
            const inviteLabel =
              invite.displayName || invite.name || invite.userNumber;

            return (
              <article
                key={invite.userNumber}
                className="hub-request-card surface-card"
              >
                <div className="hub-request-card__header">
                  <div className="hub-request-card__identity">
                    <h3>{inviteLabel}</h3>
                    <p className="product-card__meta">
                      {invite.userNumber} •{' '}
                      {msg('teams.invites.sentLabel', 'Sent')}{' '}
                      {formatInviteTime(
                        invite.createdAt,
                        locale,
                        msg('teams.moderation.unknownTime', 'Unknown time'),
                      )}
                    </p>
                  </div>

                  <HubPill tone="warning">
                    {msg('teams.joinPanel.pendingBadge', 'Pending')}
                  </HubPill>
                </div>

                {invite.message ? (
                  <p className="hub-request-card__message">{invite.message}</p>
                ) : null}

                <div className="auth-form__actions">
                  <button
                    type="button"
                    className="app-button app-button--ghost app-button--compact"
                    onClick={() => cancelInvite(invite.userNumber)}
                    disabled={Boolean(activeInviteKey)}
                  >
                    {activeInviteKey === `${invite.userNumber}:cancel`
                      ? msg('teams.invites.cancelLoading', 'Canceling...')
                      : msg('teams.invites.cancelCta', 'Cancel invite')}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <p className="shop-panel__meta text-muted mt-4">
          {msg('teams.invites.empty', 'No pending invites from this team yet.')}
        </p>
      )}
    </section>
  );
}
